import { useEffect, useState } from "react";
import { UserInfoResponseDto, UserInfoUserType } from "@/lib/api/interfaces/user-profile.interface";

/**
 * Helper to safely read from localStorage.
 */
const getSavedDepartment = () => {
  if (typeof window !== "undefined") {
    return window.localStorage.getItem("currentDepartment") || undefined;
  }
  return undefined;
};

/**
 * Helper to safely write to or remove from localStorage.
 */
const saveDepartment = (deptId?: string) => {
  if (typeof window !== "undefined") {
    if (deptId) {
      window.localStorage.setItem("currentDepartment", deptId);
    } else {
      window.localStorage.removeItem("currentDepartment");
    }
  }
};

/**
 * Custom hook to manage department selection and persistence.
 * Re-evaluates available departments whenever the user changes.
 */
export const useDepartmentManager = (user?: UserInfoResponseDto) => {
  const [currentDepartment, setCurrentDepartment] = useState<string | undefined>(getSavedDepartment);
  const [availableDepartments, setAvailableDepartments] = useState<string[]>([]);

  // Initialize department list and selection when user changes
  useEffect(() => {
    // Only for customer users who have departments
    if (user && user.userType === UserInfoUserType.CUSTOMER_USER) {
      const allowedFromBackend = Array.isArray(user.departments)
        ? Array.from(new Set(user.departments.filter(Boolean)))
        : [];
      const concreteDepartments = allowedFromBackend.filter((department) => department !== "generally");
      setAvailableDepartments(allowedFromBackend);

      const saved = getSavedDepartment();
      // Heal the legacy case where the UI previously hid the switcher for exactly
      // "generally" + one real department and users got stuck on the saved "generally" state.
      const shouldPreferConcreteDepartment =
        saved === "generally" && allowedFromBackend.includes("generally") && concreteDepartments.length === 1;

      // Ensure the saved department is still allowed, otherwise default to the first concrete one.
      const initial =
        saved && allowedFromBackend.includes(saved) && !shouldPreferConcreteDepartment
          ? saved
          : concreteDepartments[0] ?? allowedFromBackend[0];
      
      setCurrentDepartment(initial);
      saveDepartment(initial);

      if (!initial && saved) {
        // Clean up saved but invalid selection
        saveDepartment(undefined);
      }
    } else {
      setAvailableDepartments([]);
      setCurrentDepartment(undefined);
    }
  }, [user]);

  // Persist on manual change
  useEffect(() => {
    if (currentDepartment) {
      saveDepartment(currentDepartment);
    }
  }, [currentDepartment]);

  return {
    currentDepartment,
    setCurrentDepartment,
    availableDepartments,
  };
};
